import { useState, useRef, useEffect } from 'react';
import { Send, Pin, Trash2, SmilePlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import useWebinarStore from '@/stores/webinarStore';

const EMOJI_QUICK = ['👍', '❤️', '😂', '👏', '🔥', '🎉', '💡', '🤔'];

export default function LiveChat({ currentUser }) {
  const { messages, addMessage, socket, webinarId, myRole } = useWebinarStore();
  const [input, setInput] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const bottomRef = useRef(null);
  const isHost = ['host', 'co-host', 'moderator'].includes(myRole);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = (e) => {
    e.preventDefault();
    if (!input.trim() || !socket) return;

    socket.emit('webinar:chat', {
      content: input.trim(),
      type: 'chat',
      user_avatar: currentUser?.avatar,
    });
    setInput('');
  };

  const handlePin = (msgId, isPinned) => {
    socket?.emit('webinar:chat:pin', { message_id: msgId, is_pinned: !isPinned });
  };

  const handleDelete = (msgId) => {
    socket?.emit('webinar:chat:delete', { message_id: msgId });
  };

  const pinnedMessages = messages.filter(m => m.is_pinned);

  return (
    <div className="flex flex-col h-full">
      {/* Pinned messages */}
      {pinnedMessages.length > 0 && (
        <div className="px-3 py-2 border-b border-zinc-800 bg-amber-500/5">
          {pinnedMessages.map(m => (
            <div key={m.id} className="flex items-start gap-2 text-xs">
              <Pin className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />
              <div>
                <span className="font-semibold text-amber-400">{m.user_name}</span>
                <span className="text-zinc-400 ml-1">{m.content}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1 scrollbar-thin">
        <AnimatePresence initial={false}>
          {messages.map(msg => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, height: 0 }}
              className={`group py-1.5 ${msg.type === 'system' ? 'text-center' : ''}`}
            >
              {msg.type === 'system' ? (
                <span className="text-[11px] text-zinc-600 italic">{msg.content}</span>
              ) : msg.type === 'question' ? (
                <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg p-2">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-[10px] font-semibold text-violet-400">❓ Question</span>
                    <span className="text-[10px] text-zinc-500">{msg.user_name}</span>
                  </div>
                  <p className="text-xs text-zinc-300">{msg.content}</p>
                </div>
              ) : (
                <div className="flex items-start gap-2 group/msg hover:bg-zinc-800/30 rounded px-1 -mx-1 transition-colors">
                  {/* Avatar */}
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-cyan-600 to-violet-600 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-[9px] font-bold text-white">
                      {(msg.user_name || '?')[0].toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-1.5">
                      <span className={`text-xs font-semibold ${
                        msg.user_id === currentUser?.id ? 'text-cyan-400' : 'text-zinc-300'
                      }`}>{msg.user_name}</span>
                      <span className="text-[10px] text-zinc-600">
                        {new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-400 break-words">{msg.content}</p>
                  </div>
                  {/* Actions */}
                  {isHost && (
                    <div className="hidden group-hover/msg:flex items-center gap-0.5 shrink-0">
                      <button onClick={() => handlePin(msg.id, msg.is_pinned)}
                        className="p-1 rounded hover:bg-zinc-700 text-zinc-500 hover:text-amber-400 transition-colors">
                        <Pin className="w-3 h-3" />
                      </button>
                      <button onClick={() => handleDelete(msg.id)}
                        className="p-1 rounded hover:bg-zinc-700 text-zinc-500 hover:text-red-400 transition-colors">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-zinc-800">
        <form onSubmit={handleSend} className="flex items-center gap-2">
          <div className="relative">
            <button type="button" onClick={() => setShowEmoji(!showEmoji)}
              className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors">
              <SmilePlus className="w-4 h-4" />
            </button>
            {showEmoji && (
              <div className="absolute bottom-full left-0 mb-2 p-2 bg-zinc-900 border border-zinc-800 rounded-xl shadow-xl flex gap-1 z-50">
                {EMOJI_QUICK.map(e => (
                  <button key={e} type="button"
                    onClick={() => { setInput(prev => prev + e); setShowEmoji(false); }}
                    className="w-8 h-8 rounded-lg hover:bg-zinc-800 flex items-center justify-center text-base transition-colors">
                    {e}
                  </button>
                ))}
              </div>
            )}
          </div>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Écrire un message..."
            className="flex-1 bg-zinc-800/50 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 focus:border-cyan-500/50"
            maxLength={500}
          />
          <Button type="submit" size="icon" disabled={!input.trim()}
            className="h-9 w-9 bg-cyan-600 hover:bg-cyan-700 text-white disabled:opacity-30">
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
